import {Action, Card} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {useState} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTireIdsQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds, tiresInventoryRoutes} from '@dms/routes';

import {buildArray, composePath} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {DmsDataGrid, type DmsDataGridProps} from '../DmsDataGrid/DmsDataGrid';
import {TireSetDetail} from '../TireSetDetail/TireSetDetail';

export interface TiresProps extends Pick<DmsDataGridProps, 'maximization'> {
  vehicleId?: string;
  customerId?: string;
  gridCode:
    | 'tire-order-wheel-service-case'
    | 'tire-order-wheel-service-case-creating'
    | 'vehicle-tire-list'
    | 'customer-tire-list';
  createNewLink?: string;
}

const PREDEFINED_COLUMNS = [
  'tireManufacturer',
  'tireSeason',
  'orderId',
  'orderSetId',
  'setNumber',
] as const;

export function Tires(props: TiresProps) {
  const [canReadPneuhotel] = usePermissions({permissionKeys: ['pneuhotelRead']});

  const [editTireSetData, setEditTireSetData] = useState<{
    orderId: string;
    setId: string;
    title: string;
    refreshData: () => void;
  } | null>(null);

  const tireOrderIdsQuery = useGetTireIdsQuery({
    customerId: props.customerId,
    vehicleId: props.vehicleId,
  });

  const firstTireOrder = tireOrderIdsQuery.data?.[0];
  const hasTireOrder = isNotNilOrEmpty(firstTireOrder);

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {
        vehicleId: props.vehicleId ? props.vehicleId : undefined,
        customerId: props.customerId ? props.customerId : undefined,
      },
    ]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    getColumnData,
    refreshData,
  }) => {
    const tireManufacturer = getColumnData<{label: string}>('tireManufacturer')?.value?.label;
    const tireSeason = getColumnData<{label: string}>('tireSeason')?.value?.label;
    const orderId = getColumnData<string>('orderId')?.value;
    const orderSetId = getColumnData<string>('orderSetId')?.value;
    const setNumber = getColumnData<string>('setNumber')?.value;

    if (actionKey === 'redirectDetail') {
      const titleData = [tireManufacturer, tireSeason].filter(isNotNilOrEmpty);

      if (isNil(orderId) || isNil(orderSetId)) {
        throw new Error('orderId or orderSetId is undefined');
      }

      setEditTireSetData({
        orderId,
        setId: orderSetId,
        title: `${setNumber} - ${titleData.join(', ')}`,
        refreshData,
      });
    }
  };

  const actions = buildArray<Action>()
    .whenNot(canReadPneuhotel, {
      type: 'button',
      variant: 'link',
      leftIcon: 'action/open_in_new',
      title: i18n.t('page.tiresInventory.labels.tiresInventory'),
      onClick: () => window.open(tiresInventoryRoutes.overview, '_blank'),
    })
    .when(canReadPneuhotel && hasTireOrder, {
      type: 'button',
      variant: 'link',
      leftIcon: 'action/open_in_new',
      title: i18n.t('page.tiresInventory.labels.tiresInventory'),
      onClick: () => {
        window.open(
          composePath(tiresInventoryRoutes.tireOrderDetail, {
            params: {id: tireOrderIdsQuery.data?.[0]?.orderId},
            isSearchParamsPreserved: false,
          }),
          '_blank'
        );
      },
    })
    .when(canReadPneuhotel && !hasTireOrder && props.createNewLink, {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/add_circle',
      title: i18n.t('page.tiresInventory.labels.createTireOrder'),
      onClick: () => {
        window.open(props.createNewLink, '_blank');
      },
    });

  return (
    <>
      <Box data-testid={testIds.workshop.serviceCaseDetail('tires')}>
        <Card title={i18n.t('page.tiresInventory.labels.tires')} actions={actions}>
          <DmsDataGrid
            maximization={props.maximization}
            data-testid="tires-grid"
            gridCode={props.gridCode}
            autoHeight
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
          />
        </Card>
      </Box>

      <Show when={editTireSetData}>
        {(editTireSetData) => (
          <TireSetDetail
            tireOrderId={editTireSetData.orderId}
            tireSetId={editTireSetData.setId}
            onClose={() => setEditTireSetData(null)}
            onEdit={() => {
              editTireSetData.refreshData();
              setEditTireSetData(null);
            }}
          />
        )}
      </Show>
    </>
  );
}
